import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import Pagination from "utils/Pagination";
import usePublicHolidays from "./usePublicHolidays";
import PublicHolidayFormModal from "./PublicHolidayFormModal";
import ArchiveHolidayModal from "./ArchiveHolidayModal";
import PublicHolidaysTable from "./PublicHolidaysTable";
import { PUBLIC_HOLIDAYS_MESSAGES, PUBLIC_HOLIDAYS_PAGE_SIZE } from "./publicHolidays.constants";

function PublicHolidays() {
  const {
    auth: { admin },
  } = useSelector((state) => state);

  const {
    holidays,
    isLoading,
    errorMessage,
    successMessage,
    fetchHolidays,
    createHoliday,
    createHolidaysInRange,
    updateHoliday,
    archiveHoliday,
    clearError,
    clearSuccess,
    setErrorMessage,
  } = usePublicHolidays(admin?.id);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayToArchive, setHolidayToArchive] = useState({ id: null, name: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState(String(moment().year()));
  const [currentPage, setCurrentPage] = useState(1);

  const yearOptions = useMemo(() => {
    const years = new Set([String(moment().year())]);
    holidays.forEach((holiday) => years.add(String(moment(holiday.date).year())));
    return [
      { value: "all", label: "All years" },
      ...Array.from(years)
        .sort()
        .map((year) => ({ value: year, label: year })),
    ];
  }, [holidays]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...holidays]
      .filter((holiday) => yearFilter === "all" || String(moment(holiday.date).year()) === yearFilter)
      .filter((holiday) => !term || (holiday.name || "").toLowerCase().includes(term))
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
  }, [holidays, search, yearFilter]);

  const pageRows = visible.slice(
    (currentPage - 1) * PUBLIC_HOLIDAYS_PAGE_SIZE,
    currentPage * PUBLIC_HOLIDAYS_PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, yearFilter]);

  useEffect(() => {
    fetchHolidays();
  }, [admin?.id]);

  const openAddModal = () => {
    setEditingHoliday(null);
    setShowFormModal(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingHoliday(null);
  };

  const handleFormSave = async ({ name, dates }) => {
    if (!admin?.id) {
      setErrorMessage(PUBLIC_HOLIDAYS_MESSAGES.adminNotFound);
      return;
    }

    if (!dates || !dates.length) {
      setErrorMessage(PUBLIC_HOLIDAYS_MESSAGES.atLeastOneDate);
      return;
    }

    setIsSubmitting(true);
    let ok = false;
    if (editingHoliday) {
      ok = await updateHoliday({ id: editingHoliday.id, name, date: dates[0] });
    } else {
      if (dates.length === 1) {
        ok = await createHoliday({ name, date: dates[0] });
      } else {
        ok = await createHolidaysInRange({ name, dates });
      }
    }
    setIsSubmitting(false);
    if (ok) {
      closeFormModal();
    }
  };

  const openArchiveModal = (holiday) => {
    setHolidayToArchive({ id: holiday.id, name: holiday.name });
    setShowArchiveModal(true);
  };

  const closeArchiveModal = () => {
    setShowArchiveModal(false);
    setHolidayToArchive({ id: null, name: null });
  };

  const handleArchiveHoliday = async () => {
    if (!holidayToArchive.id) return;
    setIsSubmitting(true);
    const ok = await archiveHoliday({ id: holidayToArchive.id });
    setIsSubmitting(false);
    if (ok) {
      closeArchiveModal();
    }
  };

  return (
    <SuiBox>
      {errorMessage && (
        <ErrorAlert
          color="error"
          message={errorMessage}
          clearError={clearError}
        />
      )}
      {successMessage && (
        <ErrorAlert
          color="success"
          message={successMessage}
          clearError={clearSuccess}
        />
      )}
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <SuiBox>
          <SuiTypography variant="h5" fontWeight="medium">
            Public Holidays
          </SuiTypography>
          <SuiTypography variant="caption" color="text" display="block">
            Days the whole team is off. These are excluded from attendance reports.
          </SuiTypography>
        </SuiBox>
        <SuiBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <SuiBox minWidth="220px">
            <SuiInput
              placeholder="Search holidays..."
              icon={{ component: "search", direction: "left" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SuiBox>
          <SuiBox minWidth="140px">
            <CustomSelect
              options={yearOptions}
              value={yearOptions.find((option) => option.value === yearFilter) || yearOptions[0]}
              onChange={(option) => setYearFilter(option.value)}
              customStyles={goalSelectStyles}
              menuPortalTarget={document.body}
            />
          </SuiBox>
          <GoalButton variant="primary" icon="add" onClick={openAddModal}>
            Add holiday
          </GoalButton>
        </SuiBox>
      </SuiBox>
      <PublicHolidaysTable
        holidays={pageRows}
        onEdit={openEditModal}
        onArchive={openArchiveModal}
        isLoading={isLoading}
        emptyMessage={
          holidays.length ? PUBLIC_HOLIDAYS_MESSAGES.emptyFiltered : PUBLIC_HOLIDAYS_MESSAGES.empty
        }
      />
      {!isLoading && (
        <SuiBox mt={3}>
          <Pagination
            currentPage={currentPage}
            totalCount={visible.length}
            pageSize={PUBLIC_HOLIDAYS_PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </SuiBox>
      )}

      <PublicHolidayFormModal
        open={showFormModal}
        onClose={closeFormModal}
        onSave={handleFormSave}
        isSubmitting={isSubmitting}
        initialHoliday={editingHoliday}
        rangeModeEnabled
      />

      <ArchiveHolidayModal
        open={showArchiveModal}
        onClose={closeArchiveModal}
        onConfirm={handleArchiveHoliday}
        holidayName={holidayToArchive.name}
        isSubmitting={isSubmitting}
      />
    </SuiBox>
  );
}

export default PublicHolidays;
